import React from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";
import { isAdminRole } from "../../utils/roles";
import { taskDaysOverdue, taskDueDay } from "../../utils/taskDue";
import { Button } from "../ui";

interface Action {
  id: string;
  title: string;
  description?: string;
  due_date: string | null;
  status: string;
  priority: string;
  // A task need not be about a membership.
  membership_id: string | null;
  action_type?: {
    name: string;
    color: string;
  };
  membership?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

interface OverdueActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  overdueActions: Action[];
}

const OverdueActionsModal: React.FC<OverdueActionsModalProps> = ({
  isOpen,
  onClose,
  overdueActions,
}) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  if (!isOpen || overdueActions.length === 0) return null;

  const handleActionClick = (action: Action) => {
    const tenantSlug = localStorage.getItem("tenantSlug");

    // Close modal first
    onClose();

    // A task about a membership opens that membership's Actions tab; any other
    // task opens in My Tasks, which fetches it even when it is not on the list.
    if (!action.membership_id) {
      navigate(`/${tenantSlug}/manage/tasks?taskId=${encodeURIComponent(action.id)}`);
      return;
    }
    navigate(`/${tenantSlug}/manage/memberships`, {
      state: { redirectedFrom: 'overdueAction', memberId: action.membership_id, tab: 'actions' }
    });
  };

  const handleViewAll = () => {
    const tenantSlug = localStorage.getItem("tenantSlug");
    const userRole = localStorage.getItem("userRole");
    const basePath = isAdminRole(userRole) ? "admin" : "staff";

    onClose();
    navigate(`/${tenantSlug}/${basePath}/view-memberships`);
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      urgent: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      normal: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      low: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400",
    };
    return colors[priority] || colors.normal;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className={`relative w-full max-w-lg rounded-lg shadow-xl ${getCardBackground(
          isDarkMode
        )}`}
      >
        {/* Header */}
        <div className={`px-6 py-4 rounded-t-lg border-b ${getLightBorder(isDarkMode)} ${getPrimaryText(isDarkMode)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">Overdue Tasks</h2>
                <p className="text-sm opacity-80">
                  You have {overdueActions.length} overdue task{overdueActions.length > 1 ? "s" : ""} requiring attention
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              iconOnly
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
        </div>

        {/* Actions List */}
        <div className="max-h-80 overflow-y-auto px-6 py-4">
          {overdueActions.slice(0, 5).map((action) => {
            // The due DAY (utils/taskDue), never the instant: a date-only
            // follow-up is stored at midnight UTC, the previous evening locally.
            const dueDay = taskDueDay(action.due_date);
            const daysOverdue = taskDaysOverdue(action.due_date);
            return (
              <button
                key={action.id}
                onClick={() => handleActionClick(action)}
                className={`w-full text-left p-4 mb-3 last:mb-0 rounded-lg border transition-all ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {action.action_type && (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            backgroundColor: `${action.action_type.color}20`,
                            color: action.action_type.color,
                          }}
                        >
                          {action.action_type.name}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityBadge(action.priority)}`}>
                        {action.priority}
                      </span>
                    </div>
                    <h3 className={`font-medium ${getPrimaryText(isDarkMode)} truncate`}>
                      {action.title}
                    </h3>
                    {action.membership && (
                      <p className={`text-sm ${getMutedText(isDarkMode)}`}>
                        {action.membership.first_name} {action.membership.last_name}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      {daysOverdue} day{daysOverdue !== 1 ? "s" : ""} overdue
                    </span>
                    {dueDay && (
                      <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
                        Due: {format(parseISO(dueDay), "MMM d")}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {overdueActions.length > 5 && (
            <p className={`text-center text-sm ${getMutedText(isDarkMode)} mt-2`}>
              And {overdueActions.length - 5} more overdue task{overdueActions.length - 5 > 1 ? "s" : ""}...
            </p>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-800"
              }`}
            >
              Remind Me Later
            </button>
            <button
              onClick={handleViewAll}
              className="flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
            >
              View Memberships
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverdueActionsModal;
